import { useState } from "react";
import { Reveal, Eyebrow, PageHero, OutlineLink } from "@/components/shared";
import { Landmark, Smartphone, ReceiptText, ShieldCheck } from "lucide-react";
import { EMAIL } from "@/data/content";
import { useLang } from "@/i18n";

const DetailRow = ({ label, value, testId }) => (
  <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-6 py-4 border-b border-[rgba(118,87,45,0.3)]" data-testid={testId}>
    <span className="font-sans text-[12px] font-bold uppercase tracking-[0.18em] text-earth w-44 shrink-0">{label}</span>
    <span className="font-sans font-semibold text-charcoal text-[16px]">{value}</span>
  </div>
);

const Donate = ({ initialTab = "online" }) => {
  const { t } = useLang();
  const [tab, setTab] = useState(initialTab);

  const tabs = [
    { id: "online", label: t("donate.tab.online"), icon: Smartphone },
    { id: "bank-transfer", label: t("donate.tab.bank"), icon: Landmark },
    { id: "receipt", label: t("donate.tab.receipt"), icon: ReceiptText },
  ];

  const receiptSteps = [
    t("donate.receipt.step1"),
    t("donate.receipt.step2"),
    t("donate.receipt.step3"),
    t("donate.receipt.step4"),
  ];

  return (
    <>
      <PageHero
        testId="donate-hero"
        eyebrow={t("donate.heroEyebrow")}
        title={t("donate.heroTitle")}
        intro={t("donate.heroIntro")}
      />

      <section className="bg-ivory" data-testid="donate-details-section">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 py-16 lg:py-24">
          <Reveal>
            <div className="flex flex-wrap gap-2 border-b border-[rgba(118,87,45,0.35)]" role="tablist" aria-label="Donation methods">
              {tabs.map((tabItem) => (
                <button
                  key={tabItem.id}
                  role="tab"
                  aria-selected={tab === tabItem.id}
                  onClick={() => setTab(tabItem.id)}
                  data-testid={`donate-tab-${tabItem.id}`}
                  className={`inline-flex items-center gap-2 font-sans font-semibold text-[14px] px-5 py-3 min-h-[48px] rounded-t-lg transition-colors duration-200 ${
                    tab === tabItem.id
                      ? "bg-charcoal text-ivory"
                      : "text-charcoal/70 hover:text-charcoal"
                  }`}
                >
                  <tabItem.icon size={17} strokeWidth={1.75} aria-hidden="true" />
                  {tabItem.label}
                </button>
              ))}
            </div>
          </Reveal>

          <Reveal className="mt-10">
            {tab === "online" && (
              <div data-testid="donate-panel-online">
                <Eyebrow>{t("donate.online.eyebrow")}</Eyebrow>
                <h2 className="text-[28px] sm:text-3xl font-semibold text-charcoal">{t("donate.online.heading")}</h2>
                <div className="mt-8">
                  <DetailRow label={t("donate.online.trustLabel")} value={t("donate.online.trustValue")} testId="donate-upi-trust-name" />
                  <DetailRow label={t("donate.online.upiLabel")} value={t("donate.online.upiValue")} testId="donate-upi-id" />
                </div>
                <p className="mt-6 text-charcoal/75 text-[15px] leading-relaxed">
                  {t("donate.online.note1")}{" "}
                  <a href={`mailto:${EMAIL}?subject=Donation%20—%20UPI%20Details`} className="text-saffron font-semibold no-underline hover:underline" data-testid="donate-upi-email-link">
                    {EMAIL}
                  </a>{" "}
                  {t("donate.online.note2")}
                </p>
              </div>
            )}

            {tab === "bank-transfer" && (
              <div id="bank-transfer" data-testid="donate-panel-bank">
                <Eyebrow>{t("donate.bank.eyebrow")}</Eyebrow>
                <h2 className="text-[28px] sm:text-3xl font-semibold text-charcoal">{t("donate.bank.heading")}</h2>
                <div className="mt-8">
                  <DetailRow label={t("donate.online.trustLabel")} value={t("donate.online.trustValue")} testId="donate-bank-trust-name" />
                  <DetailRow label={t("donate.bank.bankNameLabel")} value={t("donate.bank.bankNameValue")} testId="donate-bank-name" />
                  <DetailRow label={t("donate.bank.accountLabel")} value="208884466444" testId="donate-account-number" />
                  <DetailRow label={t("donate.bank.ifscLabel")} value="ESFB0003005" testId="donate-ifsc" />
                  <DetailRow label={t("donate.bank.branchLabel")} value={t("donate.bank.branchValue")} testId="donate-branch" />
                  <DetailRow label={t("donate.bank.addressLabel")} value={t("donate.bank.addressValue")} testId="donate-bank-address" />
                </div>
                <p className="mt-6 text-charcoal/75 text-[15px] leading-relaxed">
                  {t("donate.bank.note1")}{" "}
                  <a href={`mailto:${EMAIL}?subject=Donation%20—%20Bank%20Transfer`} className="text-saffron font-semibold no-underline hover:underline" data-testid="donate-bank-email-link">
                    {EMAIL}
                  </a>{" "}
                  {t("donate.bank.note2")}
                </p>
              </div>
            )}

            {tab === "receipt" && (
              <div id="receipt" data-testid="donate-panel-receipt">
                <Eyebrow>{t("donate.receipt.eyebrow")}</Eyebrow>
                <h2 className="text-[28px] sm:text-3xl font-semibold text-charcoal">{t("donate.receipt.heading")}</h2>
                <p className="mt-6 text-charcoal/80 leading-relaxed">{t("donate.receipt.body")}</p>
                <ol className="mt-6 space-y-4 list-none" data-testid="receipt-instructions">
                  {receiptSteps.map((step, i) => (
                    <li key={i} className="flex gap-4 items-start">
                      <span className="font-serif text-xl font-semibold text-saffron w-8 shrink-0">{i + 1}.</span>
                      <span className="font-sans text-charcoal/85 text-[15.5px]">{step}</span>
                    </li>
                  ))}
                </ol>
                <p className="mt-6 text-charcoal/75 text-[15px]">
                  <a href={`mailto:${EMAIL}?subject=Donation%20Receipt`} className="text-saffron font-semibold no-underline hover:underline" data-testid="donate-receipt-email-link">
                    {EMAIL}
                  </a>{" "}
                  {t("donate.receipt.note1")} {t("donate.receipt.note2")}
                </p>
              </div>
            )}
          </Reveal>

          <Reveal className="mt-12 card-ivory p-6 flex items-start gap-4" data-testid="donate-80g-notice">
            <ShieldCheck size={26} strokeWidth={1.5} className="text-saffron shrink-0 mt-1" aria-hidden="true" />
            <p className="font-sans text-[15px] text-charcoal/85 leading-relaxed">{t("donate.trustNotice")}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-coal text-white" data-testid="donate-closing-section">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 py-20 text-center">
          <Reveal>
            <div className="w-16 h-px bg-saffron mx-auto mb-10" aria-hidden="true" />
            <blockquote className="font-serif text-2xl sm:text-3xl leading-snug">
              {t("donate.quote")}
            </blockquote>
            <p className="mt-8 font-sans text-sm tracking-[0.18em] uppercase text-saffron-light">{t("donate.quoteAttr")}</p>
            <div className="mt-10">
              <OutlineLink to="/contact" testId="donate-questions-button">{t("donate.contactCta")}</OutlineLink>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default Donate;
